export type ImageLoaderCallback = (image: HTMLImageElement, observer: ImageLoader) => void;

export class ImageLoader {
  static shared = new ImageLoader();
  static MAX_IMAGE_REQUESTS = 8;

  private activeRequests = 0;

  private imageQueue: string[] = [];
  private imageCache = new Map<string, HTMLImageElement>();
  private imageLoading = new Map<string, ImageLoaderCallback[]>();
  private loadingImgs = new Map<string, HTMLImageElement>();

  cancelRequest(path: string, callback: ImageLoaderCallback) {
    const callbacks = this.imageLoading.get(path);
    if (!callbacks) return;

    for (let i = 0, n = callbacks.length; i < n; ++i) {
      const waiting = callbacks[i];
      if (waiting === callback) callbacks.splice(i, 1);
    }

    if (callbacks.length === 0) {
      this.imageLoading.delete(path);

      const lastIndex = this.imageQueue.lastIndexOf(path);
      if (lastIndex >= 0) this.imageQueue.splice(lastIndex, 1);

      const img = this.loadingImgs.get(path);

      // Cancel the download and start a queued request
      if (img) {
        img.src = "";
        this.loadingImgs.delete(path);

        this.activeRequests -= 1;
        this.dequeueRequest();
      }
    }
  }

  dequeueRequest() {
    if (this.activeRequests >= ImageLoader.MAX_IMAGE_REQUESTS) {
      return;
    }

    const nextPath = this.imageQueue.shift();
    if (nextPath) this.executeRequest(nextPath);
  }

  executeRequest(path: string) {
    this.activeRequests += 1;
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => {
      this.loadingImgs.delete(path);
      this.imageCache.set(path, img);

      const callbacks = this.imageLoading.get(path);
      this.imageLoading.delete(path);
      for (let callback of callbacks || []) callback(img, this);

      this.activeRequests -= 1;
      this.dequeueRequest();

      /* Possibly - Evict the tile from the image cache - we don't want to
         store too many if it breaks older mobile devices with low memory.
         Don't do this if we don't need it - so test first on small memory.
      window.setTimeout(() => {
        const callbacks = this.imageLoading.get(path) || [];
        if (callbacks.length === 0)
          this.imageCache.delete(path);
      }, 5);
      */
    };
    img.src = path;
    this.loadingImgs.set(path, img);
  }

  getImage(tilePath: string, callback: ImageLoaderCallback) {
    const cachedImage = this.imageCache.get(tilePath);
    if (cachedImage) return cachedImage;

    const callbacks = this.imageLoading.get(tilePath);
    if (callbacks) {
      callbacks.push(callback);
      this.dequeueRequest();
      return null;
    }

    this.imageLoading.set(tilePath, [callback]);
    this.imageQueue.push(tilePath);
    this.dequeueRequest();

    return null;
  }
}

function findPreferredResolution(zoomScale: number, resolutions: number[]) {
  let minDelta = Number.POSITIVE_INFINITY;
  let bestResolution = 0;
  for (let i = 0, n = resolutions.length; i < n; ++i) {
    const delta = Math.abs(resolutions[i] * zoomScale - 1);
    if (delta < minDelta) {
      bestResolution = i;
      minDelta = delta;
    }
  }

  return bestResolution;
}



// WEBPACK FOOTER //
// webpack://SFMLWeb/src/renderer/web-components/image/util/image-loader.ts